/**
 * OrderAdapter - Solución directa y funcional
 */
export class OrderAdapter {
  /**
   * Normaliza un array de órdenes con validación de integridad de datos
   * @param {Array} orders - Órdenes para normalizar
   * @param {Object} options - Opciones de validación
   * @returns {Array} Órdenes normalizadas
   */
  static normalizeMany(orders, options = {}) {
    // Validación estructural básica
    if (!Array.isArray(orders)) {
      console.error('🚨 OrderAdapter: Se intentó normalizar un objeto no válido', typeof orders);
      return [];
    }
    
    console.log('🔐 Validando integridad de datos antes de normalizar', orders.length, 'órdenes');
    
    // Filtrar datos simulados o sospechosos
    const validOrders = orders.filter(order => {
      // Validar que las fechas tienen formato Firestore o ISO válido
      const hasValidDate = this.isValidTimestampOrDate(order.createdAt || order.created_at);
      
      // Validar que el ID tenga el formato correcto (string o número)
      const hasValidId = (
        // Aceptar IDs numéricos (como los que vienen de Firestore)
        (typeof order.id === 'number' && !isNaN(order.id)) ||
        // O mantener compatibilidad con IDs en formato string
        (typeof order.id === 'string' && order.id.trim().length > 0)
      );
      
      // Validar campos obligatorios
      const hasRequiredFields = hasValidId && hasValidDate;
      
      if (!hasRequiredFields) {
        console.warn('🚫 Orden rechazada por no cumplir criterios de validación:', 
          { id: order.id, hasValidDate, hasValidId });
      }
      
      return hasRequiredFields;
    });
    
    console.log(`⚙️ Normalizando ${validOrders.length}/${orders.length} órdenes (${orders.length - validOrders.length} rechazadas)`);
    return validOrders.map(order => this.normalize(order));
  }

  /**
   * Normaliza una orden individual - Implementación directa que funciona
   */
  static normalize(order) {
    if (!order) return null;
    
    try {
      // Convertir fechas correctamente
      const createdDate = this.convertFirestoreDate(order.createdAt || order.created_at);
      const updatedDate = this.convertFirestoreDate(order.updatedAt || order.updated_at);
      
      // Cliente
      const customer = order.customer || order.buyer || {};
      const customerName = customer.name || 
                          `${String(customer.firstName || customer.first_name || '')} ${String(customer.lastName || customer.last_name || '')}`.trim() || 
                          'Cliente';

      // Normalizar número de orden
      const orderNum = String(order.orderNumber || order.number || '');
      const formattedOrderNumber = orderNum.startsWith('#') ? orderNum : `#${orderNum}`;
      
      // Construir objeto normalizado
      const normalized = {
        id: String(order.id || ''),
        orderNumber: formattedOrderNumber,
        
        // Fechas normalizadas
        createdAt: createdDate,
        updatedAt: updatedDate,
        
        // Cliente normalizado
        customer: {
          id: String(customer.id || ''),
          name: customerName,
          email: String(customer.email || order.contact_email || ''),
          phone: String(customer.phone || order.contact_phone || '')
        },
        
        // Items/Productos
        items: this.normalizeProducts(order.products || order.items || []),
        
        // Valores monetarios
        totalPrice: this.safeNumber(order.totalPrice || order.total_price || order.total),
        discount: this.safeNumber(order.discount || order.discountTotal || order.discount_total),
        shipping: this.safeNumber(order.shipping || order.shippingCost || order.shipping_cost),
        
        // Estados normalizados
        status: String(order.status || 'pending').toLowerCase(),
        paymentStatus: String(order.paymentStatus || order.payment_status || 'pending').toLowerCase(),

        // Resolver estado de envío desde múltiples fuentes (Tiendanube y propios)
        shippingStatus: (() => {
          // 1) Campo plano ya existente
          if (order.shippingStatus || order.shipping_status) {
            return String(order.shippingStatus || order.shipping_status).toLowerCase();
          }
          // 2) Campo fulfillment_status de Tiendanube
          if (order.fulfillmentStatus || order.fulfillment_status) {
            return String(order.fulfillmentStatus || order.fulfillment_status).toLowerCase();
          }
          // 3) Primer fulfillment dentro de la colección fulfillments
          if (Array.isArray(order.fulfillments) && order.fulfillments.length) {
            const fs = order.fulfillments[0]?.status || order.fulfillments[0]?.state;
            if (fs) return String(fs).toLowerCase();
          }
          // 4) Shipping.lines u otros anidados
          if (order.shipping && typeof order.shipping === 'object') {
            const s = order.shipping.status || order.shipping.state;
            if (s) return String(s).toLowerCase();
          }
          // Por defecto, pending
          return 'pending';
        })(),
        // Guardamos también fulfillmentStatus separado si existe
        fulfillmentStatus: String(order.fulfillmentStatus || order.fulfillment_status || '').toLowerCase(),
        
        note: String(order.note || order.notes || '')
      };
      
      return normalized;
    } catch (error) {
      console.error('Error normalizando orden:', error, order);
      return order; // Devolver la original como último recurso
    }
  }

  /**
   * Convierte objeto fecha de Firestore a formato ISO
   * @param {*} firestoreDate - Fecha en cualquier formato soportado
   * @returns {string} - Fecha en formato ISO o null si no es válida
   */
  static convertFirestoreDate(firestoreDate) {
    try {
      // Si ya es string, devolver tal cual
      if (typeof firestoreDate === 'string') {
        // Validar que sea una fecha ISO válida
        const date = new Date(firestoreDate);
        if (!isNaN(date.getTime())) {
          return firestoreDate;
        }
        return null; // No es un string de fecha válido
      }
      
      // Verificar si es un timestamp de Firestore
      if (firestoreDate && typeof firestoreDate.toDate === 'function') {
        return firestoreDate.toDate().toISOString();
      }
      
      // Verificar si es un Date de JavaScript
      if (firestoreDate instanceof Date) {
        return firestoreDate.toISOString();
      }
      
      // Si es un objeto con seconds (Firestore Timestamp serializado)
      if (firestoreDate && 'seconds' in firestoreDate) {
        return new Date(firestoreDate.seconds * 1000).toISOString();
      }
      
      // Si es un objeto con _seconds (otra variante de serialización)
      if (firestoreDate && '_seconds' in firestoreDate) {
        return new Date(firestoreDate._seconds * 1000).toISOString();
      }

      // Como último recurso, intentar crear fecha
      const date = new Date(firestoreDate);
      if (!isNaN(date.getTime())) {
        return date.toISOString();
      }
      
      return null; // No se pudo convertir
    } catch (e) {
      console.error('Error convirtiendo fecha:', e, firestoreDate);
      return null;
    }
  }

  /**
   * Normaliza un valor a número de forma segura
   */
  /**
   * Valida si un valor es una fecha o timestamp válido de Firestore
   * Esta función es crítica para detectar datos falsos o simulados
   * @param {*} value - Valor a validar
   * @returns {boolean} - true si es una fecha válida, false en caso contrario
   */
  static isValidTimestampOrDate(value) {
    if (!value) return false;
    
    // Si es un string, intentar parsear como fecha ISO
    if (typeof value === 'string') {
      try {
        const date = new Date(value);
        // Validar que sea una fecha real y no inválida
        return !isNaN(date.getTime());
      } catch (e) {
        return false;
      }
    }
    
    // Si es un timestamp de Firestore con método toDate
    if (value && typeof value.toDate === 'function') {
      try {
        return !isNaN(value.toDate().getTime());
      } catch (e) {
        return false;
      }
    }
    
    // Si es una instancia de Date
    if (value instanceof Date) {
      return !isNaN(value.getTime());
    }
    
    // Si es un timestamp serializado con seconds
    if (value && typeof value === 'object' && ('seconds' in value || '_seconds' in value)) {
      try {
        const seconds = value.seconds || value._seconds;
        const date = new Date(seconds * 1000);
        return !isNaN(date.getTime());
      } catch (e) {
        return false;
      }
    }
    
    // No es ningún formato de fecha válido
    return false;
  }
  
  /**
   * Normaliza un valor a número de forma segura
   * @param {*} value - Valor a convertir a número
   * @returns {number} - Número normalizado o 0 si no es válido
   */
  static safeNumber(value) {
    if (value === undefined || value === null) return 0;
    if (typeof value === 'number') return isNaN(value) ? 0 : value;
    if (typeof value === 'string') {
      const cleaned = value.replace(/[^\d.-]/g, '');
      return Number(cleaned) || 0;
    }
    return 0;
  }
  
  /**
   * Normaliza productos/items de una orden
   */
  static normalizeProducts(products) {
    if (!Array.isArray(products)) return [];
    
    return products.map(item => ({
      id: String(item.id || ''),
      name: String(item.name || item.title || 'Producto'),
      quantity: this.safeNumber(item.quantity),
      price: this.safeNumber(item.price || item.unit_price),
      sku: String(item.sku || ''),
      variant: String(item.variant || item.variant_name || ''),
      image: item.image || null
    }));
  }
}
